<template>
  <ConfirmModal
    title="Add Exposure Entries"
    size="md"
    :action="saveEntries"
    confirmText="Save"
    :dismiss="clearstate"
    :show="show"
    @open="open"
    @closed="$emit('closed')">
    <template v-slot:button="{ props }">
      <slot
        name="button"
        v-bind="{ props }">
        <div class="text-right">
          <AppButton
            v-if="canEnter"
            secondary
            v-bind="props">
            Add Exposure Entries
          </AppButton>
          <p
            v-if="!canEnter"
            class="text-primary">
            {{ invalidEntryReason }}
          </p>
        </div>
      </slot>
    </template>

    <form class="flex flex-col gap-3 p-2">
      <!-- effective date -->
      <div>
        <DatePickerInput
          :model-value="selectedEffectiveDate"
          label="Effective Date"
          placeholder="Select date"
          @update:model-value="selectedEffectiveDateData = $event" />
      </div>

      <!-- exposure values -->
      <template
        v-for="entry in [...entries].sort((a, b) => a.exposureMetricID - b.exposureMetricID)"
        :key="entry.exposureMetricID">
        <div
          class="field-group"
          :class="{ noattr: !Array.isArray(entry.exposureAttributes) || entry.exposureAttributes.length === 0 }">
          <!-- value -->

          <NumberInput
            :model-value="entry.value"
            placeholder=" "
            :prefix="getPrefix(getTypeFromID(entry.exposureMetricID))"
            :suffix="getSuffix(getTypeFromID(entry.exposureMetricID))"
            :label="`${getTypeFromID(entry.exposureMetricID).name}`.trim()"
            :hint="hintText(entry)"
            data-test="exposure-value-input"
            :data-testid="`exposure-value-input-${entry.exposureMetricID}`"
            @update:model-value="entry.value = $event" />

          <!-- attributes -->

          <template
            v-for="attr in [...entry.exposureAttributes].sort((a, b) => a.indexPos - b.indexPos)"
            :key="attr.exposureAttributeId">
            <div :data-testid="`attr-input-${attr.exposureAttributeId}`">
              <Combobox
                v-if="attr.lookupKey && attr.options && attr.options.length >= 100"
                :model-value="attr.value != null ? String(attr.value) : ''"
                @update:model-value="attr.value = $event"
                :options="(attr.options || []).map((o) => ({ label: o.text, value: String(o.value) }))"
                :label="attr.required ? attr.name : `${attr.name} (Optional)`"
                placeholder=" " />
              <div
                v-if="attr.lookupKey && attr.options && attr.options.length >= 100 && attr.errors"
                class="text-sm text-error">
                {{ Array.isArray(attr.errors) ? attr.errors.join(', ') : attr.errors }}
              </div>

              <Combobox
                v-else-if="attr.lookupKey && attr.options && attr.options.length >= 4"
                :model-value="attr.value != null ? String(attr.value) : ''"
                @update:model-value="attr.value = $event"
                :options="(attr.options || []).map((o) => ({ label: o.text, value: String(o.value) }))"
                :label="attr.required ? attr.name : `${attr.name} (Optional)`"
                placeholder=" "
                class="p-4" />
              <div
                v-if="attr.lookupKey && attr.options && attr.options.length >= 4 && attr.errors"
                class="text-sm text-error">
                {{ Array.isArray(attr.errors) ? attr.errors.join(', ') : attr.errors }}
              </div>

              <RadioGroup
                v-else-if="attr.options && attr.options.length > 0"
                :model-value="attr.value"
                :options="(attr.options || []).map((o) => ({ label: o.text, value: o.value }))"
                layout="horizontal"
                class="gap-2"
                @update:model-value="attr.value = $event" />

              <template v-else-if="!attr.lookupKey">
                <TextField
                  :model-value="attr.value"
                  @update:model-value="attr.value = $event"
                  :label="attr.name"
                  placeholder=" "
                  :error-message="Array.isArray(attr.errors) ? attr.errors.join(', ') : attr.errors"
                  :error="!!(attr.errors && attr.errors.length)" />
              </template>
            </div>
          </template>
        </div>
      </template>

      <Alert
        v-if="note"
        type="info"
        class="mt-2"
        >{{ note }}</Alert
      >
      <div
        v-if="formError"
        class="text-primary">
        {{ formError }}
      </div>
    </form>
  </ConfirmModal>
</template>

<script>
import Alert from '@/components/v2/Alert.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Combobox from '@/components/v2/Combobox.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import DatePickerInput from '@/components/v2/DatePickerInput.vue';
import NumberInput from '@/components/v2/NumberInput.vue';
import RadioGroup from '@/components/v2/RadioGroup.vue';
import TextField from '@/components/v2/TextField.vue';
import { DateTime } from 'luxon';
import { required } from '@vuelidate/validators';
import * as Errors from '@/utilities/Errors';
import { GetPrefixSymbolFromExposureMetric, GetSuffixSymbolFromExposureMetric } from '@/utilities/Exposures';
import { compactMoney } from '@/utilities/Money';
import { isNumeric, compactNumber } from '@/utilities/Number';
import { useVuelidate } from '@vuelidate/core';

export default {
  components: { Alert, AppButton, Combobox, ConfirmModal, DatePickerInput, NumberInput, RadioGroup, TextField },
  setup() {
    return {
      v$: useVuelidate(),
    };
  },
  props: {
    exposureTypes: Array,
    canEnter: Boolean,
    insert: Function,
    invalidEntryReason: String,
    exposureData: Array,
    initialEffectiveDate: { required: false, type: String, default: '' },
    /** When set, controls modal visibility (for shared/called usage). Omit when using the trigger slot. */
    show: { type: Boolean, required: false },
    note: {
      type: String,
      required: false,
    },
  },
  emits: ['closed'],
  validations: {
    selectedEffectiveDateData: {
      required,
    },
  },
  data() {
    return {
      entries: [...this.exposureTypes],
      value: 0,
      selectedEffectiveDateData: this.initialEffectiveDate || new Date().toISOString(),
      formError: undefined,
    };
  },
  methods: {
    getPrefix: GetPrefixSymbolFromExposureMetric,
    getSuffix: GetSuffixSymbolFromExposureMetric,
    showHint(metric) {
      return !!metric.unitScale;
    },
    hintText(metric) {
      if (!this.showHint(metric)) {
        return '';
      }

      if (isNumeric(metric.value)) {
        return metric.unitOfMeasure === 'USD' ? compactMoney(metric.value) : compactNumber(metric.value);
      }
      return '';
    },
    async saveEntries() {
      this.formError = undefined;

      // clear errors
      this.entries = this.entries.map((e) => ({
        ...e,
        exposureAttributes: Array.isArray(e.exposureAttributes)
          ? e.exposureAttributes.map((a) => ({
              ...a,
              errors: undefined,
            }))
          : [],
      }));

      // validate effective date
      if (this.effectiveDateErrors.length > 0) {
        return false;
      }
      if (this.selectedEffectiveDateData === '') {
        this.v$.selectedEffectiveDateData.$touch();
        return false;
      }

      // filters out entries that are not valid
      // if a user hits save, and no values are provided then the modal just closes
      const entries = this.entries.filter((x) => x.value !== '' && !isNaN(Number(x.value)));
      entries.forEach((x) => (x.effectiveDate = this.selectedEffectiveDateData));

      // validate attributes if at least a single valid value exists
      if (entries.length > 0) {
        this.entries = entries.map((e) => ({
          ...e,
          exposureAttributes: e.exposureAttributes.map((a) => ({
            ...a,
            errors: a.required && !a.value ? Errors.FieldRequired : undefined,
          })),
        }));
      }

      // cancel the save if any errors exist
      if (this.entries.some((e) => e.exposureAttributes.some((a) => a.errors))) {
        return false;
      }

      const containsDupe = this.entries
        .filter((t) => {
          return (this.exposureData || [])
            .filter((x) => x.exposureMetricID === t.exposureMetricID)
            .some((x) => {
              return (
                DateTime.fromISO(x.effectiveDate, { zone: 'utc' }).startOf('day').toMillis() ===
                DateTime.fromISO(t.effectiveDate, { zone: 'utc' }).startOf('day').toMillis()
              );
            });
        })
        .filter((x) => ![3, 5].includes(x.exposureCategoryID));

      //check for duplicate entries by effectivedate
      if (containsDupe.length > 0) {
        const list = containsDupe.map((x) => x.name).join(', ');
        this.formError = `Error: An exposure metric ${list} already exist for this effective date.`;
        this.entries = [...this.exposureTypes];
        return false;
      }

      // insert the value and it's attributes
      return this.insert(
        entries.map((e) => ({
          value: e.value,
          exposureMetricID: e.exposureMetricID,
          effectiveDate: DateTime.fromISO(e.effectiveDate, { zone: 'utc' }).toISO(),
          exposureAttributeEntries: e.exposureAttributes.map((a) => ({
            exposureAttributeID: a.exposureAttributeId,
            value: !a.value || ('' + a.value).trim().length === 0 ? null : a.value.trim(),
          })),
        })),
      );
    },
    getTypeFromID(id) {
      return this.exposureTypes.find((x) => x.exposureMetricID === id);
    },
    clearstate() {
      this.formError = undefined;
      this.entries = this.exposureTypes.map((x) => ({ ...x, value: '' }));
      return true;
    },
    open() {
      // reset the form
      this.entries.forEach((x) => {
        if (x.exposureAttributes && x.exposureAttributes.length > 0) {
          x.exposureAttributes.forEach((a) => {
            a.value = undefined;
            a.errors = undefined;
          });
        }
      });

      /* When RadioGroup options are less than 4, set the first option as the default */

      this.entries.forEach((x) => {
        if (x.exposureAttributes && x.exposureAttributes.length > 0) {
          x.exposureAttributes.forEach((a) => {
            if (a.options && a.options.length > 0 && a.options.length < 4) {
              a.value = a.options[0].value;
            }
          });
        }
      });
    },
  },
  computed: {
    effectiveDateErrors() {
      if (!this.v$.selectedEffectiveDateData.$dirty) {
        return [];
      }
      const errors = [];
      if (!this.v$.selectedEffectiveDateData.required) errors.push('Effective date is required!');
      return errors;
    },
    effectiveDate() {
      return this.selectedEffectiveDateData
        ? DateTime.fromJSDate(new Date(this.selectedEffectiveDateData), { zone: 'utc' }).toLocaleString(DateTime.DATE_SHORT)
        : '';
    },
    selectedEffectiveDate: {
      get() {
        const dt = DateTime.fromISO(this.selectedEffectiveDateData, { zone: 'utc' });
        return `${dt.year}-${dt.month.toString().padStart(2, '0')}-${dt.day.toString().padStart(2, '0')}`;
      },
      set(value) {
        this.selectedEffectiveDateData = value;
      },
    },
  },
  watch: {
    exposureTypes() {
      this.entries = [...this.exposureTypes];
    },
  },
};
</script>
